import { cva, type VariantProps } from "class-variance-authority";
import {
  Briefcase,
  Building2,
  Clock,
  Flag,
  MapPin,
  type LucideIcon,
} from "lucide-react";
import type { ReactNode, Ref } from "react";
import { useTranslation } from "react-i18next";

import type { SegmentVacancyCard } from "@/features/segments/types";
import { cn } from "@/lib/utils/cn";

const cardVariants = cva(
  "overflow-hidden rounded-lg border bg-card text-card-foreground transition-colors",
  {
    variants: {
      tone: {
        default: "border-border",
        affected: "[border-color:var(--badge-warning-border)]",
      },
      interactive: {
        true: "relative hover-surface-primary",
        false: "",
      },
    },
    defaultVariants: { tone: "default", interactive: false },
  },
);

type VacancyOverrideCardProps = Pick<
  VariantProps<typeof cardVariants>,
  "tone"
> & {
  vacancy: SegmentVacancyCard;
  badges?: ReactNode;
  action?: ReactNode;
  onOpen?: () => void;
  rootRef?: Ref<HTMLDivElement>;
  className?: string;
};

export function VacancyOverrideCard({
  vacancy,
  badges,
  action,
  onOpen,
  rootRef,
  tone,
  className,
}: VacancyOverrideCardProps) {
  const { t } = useTranslation();
  const title = vacancy.title || t("admin.segments.builder.vacancies.untitled");

  return (
    <div
      ref={rootRef}
      data-testid="segment-vacancy-card"
      data-vacancy-id={vacancy.vacancyId}
      className={cn(
        cardVariants({ tone, interactive: Boolean(onOpen) }),
        className,
      )}
    >
      <div className="flex items-center gap-4 px-4 py-3">
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <h3
              className="min-w-0 truncate text-base font-semibold"
              title={onOpen ? undefined : (vacancy.title ?? undefined)}
            >
              {onOpen ? (
                <button
                  type="button"
                  onClick={onOpen}
                  className="max-w-full rounded-sm text-left after:absolute after:inset-0 focus-visible:outline-none focus-visible:after:rounded-lg focus-visible:after:ring-2 focus-visible:after:ring-ring"
                  data-testid="segment-vacancy-open"
                >
                  <span
                    className="block truncate"
                    title={vacancy.title ?? undefined}
                  >
                    {title}
                  </span>
                </button>
              ) : (
                title
              )}
            </h3>
            {badges && (
              <div className="relative flex flex-wrap items-center gap-2">
                {badges}
              </div>
            )}
          </div>
          <VacancyMeta vacancy={vacancy} />
        </div>
        {action && (
          <div className="relative flex shrink-0 items-center gap-2">
            {action}
          </div>
        )}
      </div>
    </div>
  );
}

const formatHours = ({
  hoursPerWeekMin: min,
  hoursPerWeekMax: max,
  contractHours,
}: Pick<
  SegmentVacancyCard,
  "hoursPerWeekMin" | "hoursPerWeekMax" | "contractHours"
>): string | null => {
  if (min && max && min !== max) return `${min}-${max}`;
  return min || max || contractHours || null;
};

function VacancyMeta({ vacancy }: { vacancy: SegmentVacancyCard }) {
  const { t } = useTranslation();
  const hours = formatHours(vacancy);
  const items: {
    key: string;
    icon: LucideIcon;
    label: string | null;
    text: string | null;
  }[] = [
    {
      key: "field",
      icon: Briefcase,
      label: t("admin.segments.builder.fields.field"),
      text: vacancy.field,
    },
    {
      key: "hours",
      icon: Clock,
      label: t("admin.segments.builder.vacancies.hoursLabel"),
      text: hours && t("admin.segments.builder.vacancies.hours", { hours }),
    },
    {
      key: "city",
      icon: MapPin,
      label: t("admin.segments.builder.fields.vacancyLocCity"),
      text: vacancy.city,
    },
    {
      key: "company",
      icon: Building2,
      label: t("admin.segments.builder.fields.company"),
      text: vacancy.company,
    },
    {
      key: "country",
      icon: Flag,
      label: t("admin.segments.builder.fields.vacancyLocCountry"),
      text: vacancy.country,
    },
  ];

  return (
    <ul className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
      {items
        .filter((item) => item.text)
        .map(({ key, icon: Icon, label, text }) => (
          <li key={key} className="inline-flex min-w-0 items-center gap-1">
            <Icon aria-hidden className="size-3.5 shrink-0" />
            <span className="sr-only">{label}: </span>
            <span className="truncate">{text}</span>
          </li>
        ))}
    </ul>
  );
}
